/**
 * Popover de celda para la columna "Canales" de la lista de Productos.
 *
 * Mismo patrón que los de pedidos (`orderCellPopovers`): hover gris + chevron + popover al clic,
 * provisto por `ExpandableCellWrapper` en ListView.
 *
 * Por qué existe: la celda solo puede mostrar un resumen ("2 canales (Tienda, POS)"), y eso deja
 * afuera lo que el vendedor necesita saber — en cuáles está, en cuáles NO, y si algo falló al
 * publicar. Los marketplaces son canales igual que la tienda: si Mercado Libre rechazó la
 * publicación, ese error tiene que estar a un clic del producto, no escondido en otra pantalla.
 */
import React from 'react';
import { BlockStack, InlineStack, Text, Badge, Box, Link } from '@/lib/polaris';
import type { ProductMarketplaceState } from '@/lib/productsApi';

/** Campos normalizados que una fila debe exponer para activar el popover de Canales. */
export interface ProductRowChannelFields {
  /** ¿Visible en la tienda online? */
  _channel_store: boolean;
  /** ¿Disponible en el punto de venta? */
  _channel_pos: boolean;
  /** Estado en cada marketplace; ausente = nunca se publicó ahí. */
  _channel_marketplaces: ProductMarketplaceState;
}

type Tone = 'success' | 'attention' | 'critical' | undefined;

/**
 * Estado de un marketplace traducido a algo que se pueda leer de un vistazo.
 * Los estados llegan tal cual los reporta cada canal, así que se agrupan por significado en vez de
 * enumerar el vocabulario de cada uno.
 */
function marketplaceBadge(state: { status: string | null; error: string | null }): { label: string; tone: Tone } {
  const s = (state.status ?? '').toLowerCase();
  if (state.error) return { label: 'Con error', tone: 'critical' };
  if (/active|published|approved|live/.test(s)) return { label: 'Publicado', tone: 'success' };
  if (/paused|inactive/.test(s)) return { label: 'Pausado', tone: 'attention' };
  if (/closed|deleted|rejected/.test(s)) return { label: 'Dado de baja', tone: 'critical' };
  if (!s) return { label: 'Sin publicar', tone: undefined };
  return { label: state.status as string, tone: undefined };
}

/**
 * Una línea del popover. Es una función de render, no un componente: este archivo exporta
 * renderers (igual que `orderCellPopovers`), y mezclar ambas cosas rompe el fast refresh.
 */
function channelRow(props: { name: string; label: string; tone: Tone; url?: string | null; error?: string | null }): React.ReactNode {
  return (
    <BlockStack gap="050">
      <InlineStack gap="200" blockAlign="center" align="space-between" wrap={false}>
        <Text as="span" variant="bodySm">{props.name}</Text>
        <Badge tone={props.tone}>{props.label}</Badge>
      </InlineStack>
      {props.url ? (
        <Link url={props.url} target="_blank" removeUnderline>
          <Text as="span" variant="bodyXs" tone="subdued">Ver publicación</Text>
        </Link>
      ) : null}
      {/* El error es la razón por la que el vendedor abrió esto: se muestra completo, no truncado. */}
      {props.error ? <Text as="span" variant="bodyXs" tone="critical">{props.error}</Text> : null}
    </BlockStack>
  );
}

/**
 * Contenido del popover: una línea por canal, incluidos los que NO están activos. Un canal ausente
 * de la lista sería indistinguible de uno que no existe — y "acá no estás vendiendo" es justamente
 * la información que hace falta.
 */
export function makeChannelsExpandable() {
  return (item: Record<string, unknown>): React.ReactNode => {
    const store = item._channel_store === true;
    const pos = item._channel_pos === true;
    const mk = (item._channel_marketplaces as ProductMarketplaceState | undefined) ?? {};
    const meli = mk.mercadolibre;
    const fala = mk.falabella;

    return (
      <Box minWidth="240px">
        <BlockStack gap="300">
          {channelRow({
            name: 'Tienda online',
            label: store ? 'Visible' : 'Oculto',
            tone: store ? 'success' : undefined,
          })}
          {channelRow({
            name: 'Punto de venta',
            label: pos ? 'Activo' : 'Inactivo',
            tone: pos ? 'success' : undefined,
          })}
          {channelRow({
            name: 'Mercado Libre',
            ...(meli ? marketplaceBadge(meli) : { label: 'Sin publicar', tone: undefined }),
            url: meli?.url,
            error: meli?.error,
          })}
          {channelRow({
            name: 'Falabella',
            ...(fala ? marketplaceBadge(fala) : { label: 'Sin publicar', tone: undefined }),
            url: fala?.url,
            error: fala?.error,
          })}
        </BlockStack>
      </Box>
    );
  };
}
